import type { IScope } from "angular";
import { cloneDeep, isEmpty, isEqual, isNil } from "lodash";
import { finalize } from "rxjs/operators";

import { zip } from "rxjs";

import type { UIRouterGlobals } from "@uirouter/core";
import type {
    IDirectConnection,
    IEquipmentFunctionalityType,
    IEquipmentTypeSlim,
    IResponseWithLoading,
    ISiteEquipmentWithCoverages,
} from "App/Modules/Models";
import { EquipmentClass, EquipmentTypeFunctions } from "App/Modules/Models";
import type {
    IEquipmentTypeRepository,
    ISiteEquipmentRepository,
    ISiteFunctionalitiesRepository,
} from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditEquipmentDetailsComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmEquipmentTypeRepository",
        "mmSiteEquipmentRepository",
        "mmSiteFunctionalitiesRepository",
        "$uiRouterGlobals",
    ];

    public originalSiteEquipment: ISiteEquipmentWithCoverages;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public equipmentOnSite: ISiteEquipmentWithCoverages[];

    public canBeDeleted: boolean;

    public tooltip: string;

    public equipmentTypes: IEquipmentTypeSlim[];

    public isRequestForInUseLoading: boolean;

    public isEquipmentTypeCompatible: boolean;

    public resolve: IFormContainerResolve<
        {
            selectedSiteEquipment: ISiteEquipmentWithCoverages;
            siteEquipment: ISiteEquipmentWithCoverages[];
        },
        string
    >;

    public isInUseAsMasterSiteEquipment: boolean;

    public siteId: string;

    public directConnections: IResponseWithLoading<IDirectConnection[]>;

    public equipmentFunctionalityTypes: IResponseWithLoading<IEquipmentFunctionalityType[]>;

    public deleteResponse: IResponseWithLoading<void>;

    public disconnectResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    public editResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    private readonly computerInUseToolTip = "Gui.Manage.RemoveComputerToolTip";

    private readonly removeEquipmentFromMapToolTip = "Gui.Manage.RemoveEquipmentTooltip";

    private readonly removeAllDirectConnectionsToolTip = "Gui.Manage.RemoveAllDirectConnectionsTooltip";

    private readonly nvrInUseTooltip = "Gui.Manage.NvrInUseTooltip";

    constructor(
        private readonly $scope: IScope,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly $uiRouterGlobals: UIRouterGlobals,
    ) {
        this.isEquipmentTypeCompatible = true;
    }

    public $onInit() {
        this.tooltip = "";
        this.originalSiteEquipment = this.resolve.formData.selectedSiteEquipment;
        this.siteEquipment = cloneDeep(this.originalSiteEquipment);
        this.siteId = this.$uiRouterGlobals.params.siteId;
        // Only NVR/DVR for now
        this.equipmentOnSite = this.resolve.formData.siteEquipment.filter(
            (se) =>
                se.id !== this.siteEquipment.id &&
                (se.class === EquipmentClass.DigitalVideoRecorder || se.class === EquipmentClass.NetworkVideoRecorder),
        );

        this.siteEquipmentRepository
            .isInUseAsMasterSiteEquipment(this.siteId, this.originalSiteEquipment.id)
            .subscribe((response) => {
                this.isInUseAsMasterSiteEquipment = response.data;
            });

        this.equipmentTypeRepository
            .getEquipmentTypes()
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.equipmentTypes = response.data.filter((et) => et.class === this.originalSiteEquipment.class);
                if (this.equipmentTypes.length > 0) {
                    this.equipmentTypes.unshift({
                        class: this.siteEquipment.className as any,
                        id: undefined,
                        model: "None",
                    } as IEquipmentTypeSlim);
                }
            });

        this.siteEquipmentRepository
            .getDirectConnections(this.siteEquipment.siteFriendlyId, this.siteEquipment.id)
            .subscribe((response) => {
                const hasDirectConnections = response.data.length > 0;

                if (hasDirectConnections) {
                    this.tooltip = this.removeAllDirectConnectionsToolTip;
                } else if (
                    this.siteEquipment.class === EquipmentClass.NetworkVideoRecorder ||
                    this.siteEquipment.class === EquipmentClass.DigitalVideoRecorder
                ) {
                    this.setTooltipForNVR();
                } else if (this.siteEquipment.class === EquipmentClass.Computer) {
                    this.setTooltipForComputers();
                } else {
                    this.setTooltipForOtherEquipment();
                }
            });

        this.$scope.$watch("$ctrl.siteEquipment.type", (newValue: IEquipmentTypeSlim) => {
            if (isNil(newValue) || isNil(newValue.id)) {
                return;
            }

            this.isEquipmentTypeCompatible = true;

            zip(
                extendWithLoadingAndFail(
                    this.siteEquipmentRepository.getDirectConnections(this.siteId, this.siteEquipment.id),
                ),
                extendWithLoadingAndFail(
                    this.equipmentTypeRepository.getEquipmentFunctionalityTypes(this.siteEquipment.type.id),
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    ([directConnections, equipmentFuncTypes]) => {
                        this.directConnections = directConnections;
                        this.equipmentFunctionalityTypes = equipmentFuncTypes;

                        if (!directConnections.isLoading && !equipmentFuncTypes.isLoading) {
                            this.isEquipmentTypeCompatible = EquipmentTypeFunctions.checkEquipmentTypeCompability(
                                directConnections.data,
                                equipmentFuncTypes.data,
                            );
                        }
                    },
                    ([directConnections, equipmentFuncTypes]) => {
                        this.directConnections = directConnections;
                        this.equipmentFunctionalityTypes = equipmentFuncTypes;
                    },
                );
        });
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public deleteSiteEquipment() {
        extendWithLoadingAndFail(this.siteEquipmentRepository.delete(this.resolve.bindings, this.siteEquipment.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<void>) => {
                    this.deleteResponse = response;
                    if (!response.isLoading && !response.hasError) {
                        // How does projection map component know if site equipment was deleted or patched?
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }

    public disconnectEquipment() {
        extendWithLoadingAndFail(
            this.siteEquipmentRepository.disconnectEquipment(this.resolve.bindings, this.siteEquipment.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                    this.disconnectResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data as any);
                    }
                },
                (error) => {
                    this.disconnectResponse = error;
                },
            );
    }

    public editEquipmentDetails() {
        const patchObject = {} as ISiteEquipmentWithCoverages;

        if (
            (this.directConnections && this.directConnections.isLoading) ||
            (this.directConnections && this.equipmentFunctionalityTypes.isLoading)
        ) {
            return;
        }

        if (!isEqual(this.originalSiteEquipment.name, this.siteEquipment.name)) {
            patchObject.name = this.siteEquipment.name;
        }

        if (!isEqual(this.originalSiteEquipment.type, this.siteEquipment.type)) {
            patchObject.type = this.siteEquipment.type;
        }

        if (!isEqual(this.originalSiteEquipment.masterSiteEquipment, this.siteEquipment.masterSiteEquipment)) {
            patchObject.masterSiteEquipment = this.siteEquipment.masterSiteEquipment;
        }

        if (!isEqual(this.originalSiteEquipment.externalIntegrationId, this.siteEquipment.externalIntegrationId)) {
            patchObject.externalIntegrationId = this.siteEquipment.externalIntegrationId;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.siteEquipmentRepository.patch(this.resolve.bindings, this.siteEquipment.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                        this.editResponse = response;

                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data as any);
                        }
                    },
                    (error) => {
                        this.editResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }

    private setTooltipForComputers() {
        this.isRequestForInUseLoading = true;
        this.siteFunctionalitiesRepository.getFunctionalitiesForSite(this.resolve.bindings).subscribe((response) => {
            this.isRequestForInUseLoading = false;
            const isComputerInUse = response.data.some(
                (functionality) =>
                    functionality.siteEquipmentComputerProcessingOnId ===
                    this.siteEquipment.mainProcessingFunctionalityId,
            );
            if (!isComputerInUse) {
                this.canBeDeleted = true;
            } else {
                this.tooltip = this.computerInUseToolTip;
            }
        });
    }

    private setTooltipForNVR() {
        this.tooltip = this.nvrInUseTooltip;

        if (!this.isInUseAsMasterSiteEquipment) {
            this.canBeDeleted = true;
        }
    }

    private setTooltipForOtherEquipment() {
        this.tooltip = this.removeEquipmentFromMapToolTip;

        if (!this.originalSiteEquipment.positionOnMap && isEmpty(this.originalSiteEquipment.viewCoverages)) {
            this.canBeDeleted = true;
        }
    }
}
